import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { pennerManagementApi } from "../../../services/api/omni/pennermanagement";
import {
  buildPennerCacheKey,
  getPennerCache,
} from "../../../services/api/omni/pennerManagementCache";
import ArticleInsightsDialog from "../components/ArticleInsightsDialog";
import PennerSearchField from "../components/PennerSearchField";
import PennerTablePagination, {
  DEFAULT_PAGE_SIZE,
} from "../components/PennerTablePagination";
import {
  STOCK_AGE_BUCKETS,
  NO_SALE_BUCKET,
  getStockAgeBucket,
} from "../utils/stockAge";
import { useDebouncedValue } from "../utils/useDebouncedValue";

const SORT_OPTIONS_PENNER = [
  { label: "Sales (30D)", value: "sales_30d" },
  { label: "Days Since Last Sale", value: "days_since_last_sale" },
  { label: "Last Sale Date", value: "last_sale_date" },
  { label: "Reductions", value: "reduction_waves" },
  { label: "Capital Bound", value: "capital_bound" },
];

const SORT_OPTIONS_RENNER = [
  { label: "Sales (30D)", value: "sales_30d" },
  { label: "Increases", value: "reduction_waves" },
  { label: "Last Increase", value: "last_reduction_date" },
  { label: "Days Since Last Sale", value: "days_since_last_sale" },
  { label: "Capital Bound", value: "capital_bound" },
];

/** UI copy differs: Penner lowers prices; Renner raises them. API field names stay the same. */
const WAVE_COPY = {
  penner: {
    typeLabel: "Penner",
    wavesCol: "Reduction Waves",
    lastWaveCol: "Last Reduction",
    pricesCol: "Prices (Original → Current)",
    sortOptions: SORT_OPTIONS_PENNER,
    defaultSortBy: "days_since_last_sale",
    alert: (
      <>
        All <strong>Penner</strong> articles with at least one price reduction.
        The <strong>Stock Analysis</strong> shows how long it has been since the
        last sale — color coded from fresh (0-30 days) to critical (180+ days).
        Use the chart icon for sales and prices per reduction wave.
      </>
    ),
  },
  renner: {
    typeLabel: "Renner",
    wavesCol: "Increase Waves",
    lastWaveCol: "Last Increase",
    pricesCol: "Prices (Base → Current)",
    sortOptions: SORT_OPTIONS_RENNER,
    defaultSortBy: "reduction_waves",
    alert: (
      <>
        All <strong>Renner</strong> articles with at least one price increase.
        Sort by <strong>Increases</strong> to see how many upward waves ran.
        Stock Analysis still shows sales recency. Use the chart icon for sales
        and prices per increase wave.
      </>
    ),
  },
};

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value));
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatPriceRange = (min, max, single) => {
  if (single != null) return formatCurrency(single);
  if (min != null && max != null) {
    return `${formatCurrency(min)} – ${formatCurrency(max)}`;
  }
  if (min != null) return formatCurrency(min);
  if (max != null) return formatCurrency(max);
  return "—";
};

const getErrorMessage = (error) => {
  if (typeof error === "string") return error;
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === "string") return detail;
    return JSON.stringify(detail);
  }
  return error.message || "Unbekannter Fehler";
};

const LastSaleCell = ({ row }) => {
  const bucket = getStockAgeBucket(row.days_since_last_sale);
  const daysLabel =
    row.days_since_last_sale != null
      ? `${row.days_since_last_sale} Days`
      : bucket.label;

  return (
    <Box>
      <Chip
        size="small"
        label={daysLabel}
        sx={{
          bgcolor: bucket.bg,
          color: bucket.color,
          fontWeight: 600,
          mb: 0.5,
        }}
      />
      <Typography variant="caption" display="block" color="text.secondary">
        {formatDate(row.last_sale_date)}
      </Typography>
    </Box>
  );
};

const ChannelPriceDetail = ({ channels }) => {
  if (!channels?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Keine Kanalpreise konfiguriert
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Kanal</TableCell>
          <TableCell>Original</TableCell>
          <TableCell>Aktuell</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {channels.map((ch) => (
          <TableRow key={ch.channel}>
            <TableCell sx={{ textTransform: "uppercase" }}>
              {ch.channel}
            </TableCell>
            <TableCell>
              {formatPriceRange(
                ch.original_min,
                ch.original_max,
                ch.original_price,
              )}
            </TableCell>
            <TableCell>
              {formatPriceRange(
                ch.current_min,
                ch.current_max,
                ch.current_price,
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const ArticleRow = ({ row, onShowChart }) => {
  const [open, setOpen] = useState(false);
  const primaryChannel = row.channel_prices?.[0];

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            aria-label="Details"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell padding="checkbox">
          <Tooltip title="Verkaufs- und Preisverlauf">
            <IconButton
              size="small"
              onClick={() => onShowChart(row)}
              aria-label="Chart"
            >
              <ShowChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>{row.sku ?? "—"}</TableCell>
        <TableCell>{row.articlename ?? "—"}</TableCell>
        <TableCell align="right">
          {row.stock != null ? Number(row.stock).toLocaleString("de-DE") : "—"}
        </TableCell>
        <TableCell align="right">{formatCurrency(row.sales_30d)}</TableCell>
        <TableCell>
          <LastSaleCell row={row} />
        </TableCell>
        <TableCell>
          {formatDate(row.last_buy_date)}
          {row.last_buy_price != null && (
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              {formatCurrency(row.last_buy_price)}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">{formatCurrency(row.capital_bound)}</TableCell>
        <TableCell align="right">{row.reduction_waves ?? 0}</TableCell>
        <TableCell>{formatDate(row.last_reduction_date)}</TableCell>
        <TableCell>
          {primaryChannel ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {primaryChannel.channel?.toUpperCase()}
              </Typography>
              <Typography variant="body2">
                {formatPriceRange(
                  primaryChannel.original_min,
                  primaryChannel.original_max,
                  primaryChannel.original_price,
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                →{" "}
                {formatPriceRange(
                  primaryChannel.current_min,
                  primaryChannel.current_max,
                  primaryChannel.current_price,
                )}
              </Typography>
            </>
          ) : (
            "—"
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={12}
          sx={{ py: 0, borderBottom: open ? undefined : 0 }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, pl: 6 }}>
              <ChannelPriceDetail channels={row.channel_prices} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const StockAgeOverview = ({ counts, activeBucket, onBucketClick }) => {
  const chips = [
    ...STOCK_AGE_BUCKETS.map((b) => ({ ...b, count: counts?.[b.id] ?? 0 })),
    { ...NO_SALE_BUCKET, count: counts?.never ?? 0 },
  ];

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ alignSelf: "center", mr: 1 }}
      >
        Stock Analysis (Days since last sale):
      </Typography>
      {chips.map((b) => (
        <Chip
          key={b.id}
          clickable
          onClick={() => onBucketClick(b.id)}
          variant={activeBucket === b.id ? "filled" : "outlined"}
          label={`${b.label}: ${b.count}`}
          sx={{
            bgcolor: activeBucket === b.id ? b.bg : undefined,
            color: activeBucket === b.id ? b.color : undefined,
            borderColor: b.color,
            fontWeight: activeBucket === b.id ? 600 : 400,
          }}
        />
      ))}
    </Box>
  );
};

const EMPTY_STOCK_AGE = {
  fresh: 0,
  moderate: 0,
  aging: 0,
  stale: 0,
  never: 0,
};

const ArticlesTab = ({ onError, articleType = "penner" }) => {
  const copy = WAVE_COPY[articleType] || WAVE_COPY.penner;
  const typeLabel = copy.typeLabel;
  const [sortBy, setSortBy] = useState(copy.defaultSortBy);
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [articlesData, setArticlesData] = useState({
    items: [],
    total: 0,
    stock_age_counts: EMPTY_STOCK_AGE,
  });
  const [bucketFilter, setBucketFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [chartArticle, setChartArticle] = useState(null);

  useEffect(() => {
    setSortBy(copy.defaultSortBy);
    setSortDir("desc");
    setBucketFilter(null);
    setPage(0);
  }, [articleType, copy.defaultSortBy]);

  useEffect(() => {
    setPage(0);
  }, [sortBy, sortDir, debouncedSearch, bucketFilter, pageSize, articleType]);

  const fetchArticles = useCallback(async () => {
    const query = {
      sort_by: sortBy,
      sort_dir: sortDir,
      search: debouncedSearch,
      stock_age_bucket: bucketFilter,
      skip: page * pageSize,
      limit: pageSize,
      article_type: articleType,
    };
    const cacheKey = buildPennerCacheKey("articles", query);
    const cached = getPennerCache(cacheKey);
    if (cached) {
      setArticlesData({
        items: cached.items ?? [],
        total: cached.total ?? 0,
        stock_age_counts: cached.stock_age_counts ?? EMPTY_STOCK_AGE,
      });
    }

    try {
      if (!cached) {
        setLoading(true);
      }
      onError?.(null);
      const response = await pennerManagementApi.getArticles(query);
      setArticlesData({
        items: response.data?.items ?? [],
        total: response.data?.total ?? 0,
        stock_age_counts: response.data?.stock_age_counts ?? EMPTY_STOCK_AGE,
      });
    } catch (err) {
      console.error(`Failed to fetch ${articleType} articles`, err);
      onError?.(getErrorMessage(err));
      setArticlesData({
        items: [],
        total: 0,
        stock_age_counts: EMPTY_STOCK_AGE,
      });
    } finally {
      setLoading(false);
    }
  }, [
    sortBy,
    sortDir,
    debouncedSearch,
    bucketFilter,
    page,
    pageSize,
    onError,
    articleType,
  ]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleSortClick = (value) => {
    if (sortBy === value) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(value);
      setSortDir("desc");
    }
  };

  const items = articlesData.items ?? [];
  const total = articlesData.total ?? 0;
  const hasActiveFilters = Boolean(bucketFilter || searchQuery.trim());
  const isEmpty = !loading && items.length === 0;

  // Keep page in range when filters shrink the result set
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [total, page, pageSize]);

  return (
    <Box>
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
        <Typography variant="body2">{copy.alert}</Typography>
      </Alert>

      {!loading && (total > 0 || hasActiveFilters) && (
        <StockAgeOverview
          counts={articlesData.stock_age_counts}
          activeBucket={bucketFilter}
          onBucketClick={(id) =>
            setBucketFilter((prev) => (prev === id ? null : id))
          }
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
          alignItems: "center",
        }}
      >
        <PennerSearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search SKU, article name or channel (otto, fba, shopify…)…"
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Sorting:
        </Typography>
        {copy.sortOptions.map((option) => (
          <Chip
            key={option.value}
            label={`${option.label}${
              sortBy === option.value ? (sortDir === "desc" ? " ↓" : " ↑") : ""
            }`}
            clickable
            color={sortBy === option.value ? "primary" : "default"}
            variant={sortBy === option.value ? "filled" : "outlined"}
            onClick={() => handleSortClick(option.value)}
          />
        ))}
        {!loading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: "auto" }}
          >
            {total.toLocaleString("de-DE")} Artikel
          </Typography>
        )}
      </Box>

      {loading && items.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isEmpty ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {hasActiveFilters
              ? "No articles match your filters"
              : `No ${typeLabel} articles found`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters
              ? "Try a different SKU, channel name or stock-age filter."
              : `Articles need article_type = ${articleType} and entries in pricing_history.`}
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell padding="checkbox" />
                  <TableCell>SKU</TableCell>
                  <TableCell>Article</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell align="right">Sales (30D)</TableCell>
                  <TableCell>Last Sale</TableCell>
                  <TableCell>Last Purchase</TableCell>
                  <TableCell align="right">Capital Bound</TableCell>
                  <TableCell align="right">{copy.wavesCol}</TableCell>
                  <TableCell>{copy.lastWaveCol}</TableCell>
                  <TableCell>{copy.pricesCol}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <ArticleRow
                    key={row.sku}
                    row={row}
                    onShowChart={setChartArticle}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <PennerTablePagination
            count={total}
            page={page}
            rowsPerPage={pageSize}
            onPageChange={setPage}
            onRowsPerPageChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        </Paper>
      )}

      <ArticleInsightsDialog
        open={Boolean(chartArticle)}
        sku={chartArticle?.sku}
        articlename={chartArticle?.articlename}
        articleType={articleType}
        onClose={() => setChartArticle(null)}
      />
    </Box>
  );
};

export default ArticlesTab;
